// apps/client/src/components/tags/EntityTagFilter.tsx
//
// One-tag filter for the lead and client lists (eDM plan-v3 core, Slice E3;
// only rendered when `edm_content` is on). Options are the tag registry
// (edmTagAction listRegistry) grouped by kind. Filtering happens on the
// server (listLeads `tagNorm` / listClients `tagNorms`), never on the page.

import React, { useMemo } from 'react';
import type { EdmTagEntry, EdmTagKind } from '../../hooks/useEdmContacts';
import { edmErrorMessage } from '../../utils/edm';

// Same labels as EdmContactsPage's TAG_KIND_GROUP_LABEL (E2-owned file; not
// shared through utils/edm.ts because that file is S0-owned in this slice).
const KIND_ORDER: EdmTagKind[] = ['tag', 'category', 'event'];
const KIND_GROUP_LABEL: Record<EdmTagKind, string> = { tag: 'Tags', category: 'Categories', event: 'Events' };

interface Props {
  registry: EdmTagEntry[] | null;
  registryError?: unknown;
  /** Selected registry name, or null for no filter. */
  value: string | null;
  onChange: (tag: string | null) => void;
  /** Shown as a Retry link next to a registry error. */
  onRetry?: () => void;
  className?: string;
}

const DEFAULT_CLASS =
  'px-2 py-1.5 rounded-md bg-gray-900 border border-gray-700 text-white text-sm focus:outline-none focus:border-blue-500';

const EntityTagFilter: React.FC<Props> = ({ registry, registryError, value, onChange, onRetry, className }) => {
  const groups = useMemo(() => {
    const list = registry ?? [];
    return KIND_ORDER.map((kind) => ({
      kind,
      tags: list.filter((t) => t.kind === kind).sort((a, b) => a.name.localeCompare(b.name)),
    })).filter((g) => g.tags.length > 0);
  }, [registry]);

  // Keep a selected tag selectable even if it left the registry meanwhile.
  const known = (registry ?? []).some((t) => t.name === value);
  const loading = registry === null && !registryError;

  return (
    <span className="inline-flex flex-col gap-1">
      <select
        aria-label="Filter by tag"
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value || null)}
        disabled={registry === null && !value}
        className={className ?? DEFAULT_CLASS}
      >
        <option value="">{loading ? 'Loading tags…' : 'All tags'}</option>
        {value && !known && <option value={value}>{value}</option>}
        {groups.map((g) => (
          <optgroup key={g.kind} label={KIND_GROUP_LABEL[g.kind]}>
            {g.tags.map((t) => (
              <option key={t.name} value={t.name}>
                {t.name}
              </option>
            ))}
          </optgroup>
        ))}
      </select>
      {registryError ? (
        <span className="text-xs text-red-300">
          {edmErrorMessage(registryError)}
          {onRetry && (
            <>
              {' '}
              <button type="button" className="underline" onClick={onRetry}>
                Retry
              </button>
            </>
          )}
        </span>
      ) : null}
    </span>
  );
};

export default EntityTagFilter;
